import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import Sidebar from '../admin-dashboard/components/Sidebar';
import AdminBottomNav from '../admin-dashboard/components/AdminBottomNav';
import AdminMobileHeader from '../admin-dashboard/components/AdminMobileHeader';
import { calculateManagerBonus } from '../../utils/bonusCalculator';

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatCurrency = (val) => {
  if (val === null || val === undefined || isNaN(val)) return '$0.00';
  return '$' + Math.abs(Number(val)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatPeriodLabel = (period, server, t) => {
  if (!period) return '';
  const [y, m] = period.split('-');
  const monthName = t ? t(`months.${parseInt(m, 10) - 1}`) : m;
  return t ? t('payments.commissionFor', { month: monthName, year: y, server: server || '' }) : `Commission pour ${monthName} ${y} sur ${server || ''}`;
};

const getTypeConfig = (t) => ({
  commission: { label: t('admin.payments.typeLabels.commission', 'Commission'), color: '#22c55e', bg: 'rgba(34,197,94,0.15)' },
  payment: { label: t('admin.payments.typeLabels.payment', 'Paiement'), color: '#a855f7', bg: 'rgba(168,85,247,0.15)' },
  adjustment: { label: t('admin.payments.typeLabels.adjustment', 'Ajustement'), color: '#f59e0b', bg: 'rgba(245,158,11,0.15)' },
  tournament: { label: t('admin.payments.typeLabels.tournament', 'Tournoi'), color: '#ec4899', bg: 'rgba(236,72,153,0.15)' },
});

// ─── Main Component ──────────────────────────────────────────────────────────

const ManagerPayments = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  // Data
  const [managers, setManagers] = useState([]);
  const [selectedManagerId, setSelectedManagerId] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [overviewLoading, setOverviewLoading] = useState(true);

  // Modal ajout transaction
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('payment');
  const [modalAmount, setModalAmount] = useState('');
  const [modalDescription, setModalDescription] = useState('');
  const [modalDate, setModalDate] = useState(new Date().toISOString().split('T')[0]);
  const [modalSaving, setModalSaving] = useState(false);

  // Génération commissions
  const [showGenModal, setShowGenModal] = useState(false);
  const [genPeriod, setGenPeriod] = useState('');
  const [genLoading, setGenLoading] = useState(false);
  const [genResult, setGenResult] = useState(null);
  const [availablePeriods, setAvailablePeriods] = useState([]);

  // Auth
  useEffect(() => {
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      if (session?.user?.email) setUserEmail(session?.user?.email);
    });
  }, []);

  // Fetch managers
  useEffect(() => {
    const fetch = async () => {
      const { data } = await supabase
        ?.from('users')
        ?.select('id, name, email')
        ?.in('role', ['manager', 'region_manager'])
        ?.order('name');
      setManagers(data || []);
    };
    fetch();
  }, []);

  // Fetch available periods (for generation)
  useEffect(() => {
    const fetchPeriods = async () => {
      const { data } = await supabase?.rpc('get_distinct_periods');
      setAvailablePeriods(data?.map(p => p?.period) || []);
    };
    fetchPeriods();
  }, []);

  // Fetch ALL transactions for overview
  const fetchAllTransactions = useCallback(async () => {
    setOverviewLoading(true);
    const { data, error } = await supabase
      ?.from('manager_transactions')
      ?.select('manager_id, type, amount')
      ?.order('created_at', { ascending: false });
    if (error) console.error('Error fetching all transactions:', error);
    setAllTransactions(data || []);
    setOverviewLoading(false);
  }, []);

  useEffect(() => {
    fetchAllTransactions();
  }, [fetchAllTransactions]);

  // Fetch transactions for selected manager
  const fetchTransactions = useCallback(async () => {
    if (!selectedManagerId) {
      setTransactions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      ?.from('manager_transactions')
      ?.select('*')
      ?.eq('manager_id', selectedManagerId)
      ?.order('created_at', { ascending: false });
    if (error) console.error('Error fetching transactions:', error);
    setTransactions(data || []);
    setLoading(false);
  }, [selectedManagerId]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // ─── Overview: agrégats par manager ────────────────────────────────────────

  const overviewData = useMemo(() => {
    const map = {};
    managers.forEach(m => {
      map[m.id] = { id: m.id, name: m.name, commissions: 0, payments: 0, adjustments: 0, tournaments: 0 };
    });

    allTransactions.forEach(txn => {
      const entry = map[txn.manager_id];
      if (!entry) return;
      const amt = Number(txn.amount) || 0;
      if (txn.type === 'commission') entry.commissions += amt;
      else if (txn.type === 'payment') entry.payments += Math.abs(amt);
      else if (txn.type === 'adjustment') entry.adjustments += amt;
      else if (txn.type === 'tournament') entry.tournaments += amt;
    });

    return Object.values(map)
      .map(m => ({
        ...m,
        solde: m.commissions + m.adjustments + m.tournaments - m.payments,
      }))
      .sort((a, b) => b.solde - a.solde);
  }, [managers, allTransactions]);

  // Global KPIs
  const globalKpis = useMemo(() => {
    return overviewData.reduce((acc, m) => ({
      totalSolde: acc.totalSolde + m.solde,
      totalCommissions: acc.totalCommissions + m.commissions,
      totalAdjustments: acc.totalAdjustments + m.adjustments,
      totalTournaments: acc.totalTournaments + m.tournaments,
      totalPayments: acc.totalPayments + m.payments,
    }), { totalSolde: 0, totalCommissions: 0, totalAdjustments: 0, totalTournaments: 0, totalPayments: 0 });
  }, [overviewData]);

  // ─── KPIs pour manager sélectionné ─────────────────────────────────────────

  const kpis = useMemo(() => {
    let commissions = 0;
    let payments = 0;
    let adjustments = 0;
    let tournaments = 0;

    transactions.forEach(txn => {
      const amt = Number(txn.amount) || 0;
      if (txn.type === 'commission') commissions += amt;
      else if (txn.type === 'payment') payments += Math.abs(amt);
      else if (txn.type === 'adjustment') adjustments += amt;
      else if (txn.type === 'tournament') tournaments += amt;
    });

    return {
      solde: commissions + adjustments + tournaments - payments,
      commissions,
      payments,
      adjustments,
      tournaments,
    };
  }, [transactions]);

  // ─── Générer commissions ───────────────────────────────────────────────────

  const generateCommissionsForPeriod = async (period, adminId) => {
    const { data: monthlyData, error: mErr } = await supabase
      ?.from('creator_monthly_history')
      ?.select('manager_id, server, total_bonus, charge, incremental_bonus')
      ?.eq('period', period)
      ?.not('manager_id', 'is', null);

    if (mErr) throw mErr;
    if (!monthlyData?.length) return 0;

    const { data: rates } = await supabase
      ?.from('manager_rates')
      ?.select('manager_id, incremental_rate')
      ?.eq('period', period);

    const rateMap = {};
    (rates || []).forEach(r => { rateMap[r.manager_id] = r.incremental_rate; });

    const agg = {};
    monthlyData.forEach(row => {
      const key = `${row.manager_id}__${row.server}`;
      if (!agg[key]) {
        agg[key] = { manager_id: row.manager_id, server: row.server, totalBonus: 0, totalCharge: 0, totalIncremental: 0 };
      }
      agg[key].totalBonus += Number(row.total_bonus) || 0;
      agg[key].totalCharge += Number(row.charge) || 0;
      agg[key].totalIncremental += Number(row.incremental_bonus) || 0;
    });

    const { error: delErr } = await supabase
      ?.from('manager_transactions')
      ?.delete()
      ?.eq('type', 'commission')
      ?.eq('period', period);

    if (delErr) throw delErr;

    const toInsert = Object.values(agg).map(a => {
      const rate = rateMap[a.manager_id] || 50;
      const commission = calculateManagerBonus(a.totalBonus, a.totalCharge, a.totalIncremental, rate);
      return {
        manager_id: a.manager_id,
        type: 'commission',
        amount: Math.round(commission * 100) / 100,
        description: formatPeriodLabel(period, a.server, t),
        period,
        server: a.server,
        created_by: adminId,
      };
    });

    const { error: iErr } = await supabase
      ?.from('manager_transactions')
      ?.insert(toInsert);

    if (iErr) throw iErr;
    return toInsert.length;
  };

  const generateCommissions = async (allPeriods = false) => {
    if (!allPeriods && !genPeriod) return;
    setGenLoading(true);
    setGenResult(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const adminId = session?.user?.id;
      const periodsToProcess = allPeriods ? availablePeriods : [genPeriod];
      let totalCount = 0;

      for (const period of periodsToProcess) {
        const count = await generateCommissionsForPeriod(period, adminId);
        totalCount += count;
      }

      setGenResult({
        success: true,
        message: allPeriods
          ? `${totalCount} commissions générées pour ${periodsToProcess.length} périodes.`
          : `${totalCount} commissions générées pour ${genPeriod}.`,
      });

      fetchTransactions();
      fetchAllTransactions();
    } catch (err) {
      console.error(err);
      setGenResult({ success: false, message: err?.message || 'Erreur lors de la génération.' });
    }

    setGenLoading(false);
  };

  // ─── Ajouter transaction manuelle ──────────────────────────────────────────

  const handleAddTransaction = async () => {
    if (!modalAmount || !selectedManagerId) return;
    setModalSaving(true);

    const { data: { session } } = await supabase.auth.getSession();
    const amt = parseFloat(modalAmount);
    const isDebit = modalType === 'payment' || modalType === 'tournament' || modalType === 'adjustment_minus';
    const finalAmount = isDebit ? -Math.abs(amt) : Math.abs(amt);
    const finalType = modalType.startsWith('adjustment') ? 'adjustment' : modalType;

    const { error } = await supabase?.from('manager_transactions')?.insert({
      manager_id: selectedManagerId,
      type: finalType,
      amount: Math.round(finalAmount * 100) / 100,
      description: modalDescription || null,
      created_at: modalDate ? new Date(modalDate).toISOString() : new Date().toISOString(),
      created_by: session?.user?.id,
    });

    if (error) {
      console.error('Error adding transaction:', error);
      alert('Erreur: ' + error.message);
    } else {
      setShowModal(false);
      setModalAmount('');
      setModalDescription('');
      setModalDate(new Date().toISOString().split('T')[0]);
      fetchTransactions();
      fetchAllTransactions();
    }
    setModalSaving(false);
  };

  // ─── Delete transaction ────────────────────────────────────────────────────

  const handleDelete = async (id) => {
    if (!confirm('Supprimer cette transaction ?')) return;
    const { error } = await supabase?.from('manager_transactions')?.delete()?.eq('id', id);
    if (!error) {
      fetchTransactions();
      fetchAllTransactions();
    }
  };

  // ─── Selected manager info ────────────────────────────────────────────────

  const selectedManager = managers.find(m => m.id === selectedManagerId);

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex h-[100dvh] overflow-hidden" style={{ backgroundColor: 'var(--color-background)' }}>
      <div className="hidden md:flex">
        <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} userEmail={userEmail} />
      </div>

      <main className="flex-1 overflow-y-auto pb-16 md:pb-0" style={{ backgroundColor: 'var(--color-background)' }}>
        <AdminMobileHeader title={t('admin.payments.title', 'Caisse & Paiements')} />

        <div className="p-4 sm:p-6 max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
                {t('admin.payments.title', 'Caisse & Paiements')}
              </h1>
              <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.4)' }}>
                {t('admin.payments.subtitle', 'Vue globale des soldes managers et historique des transactions.')}
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setShowGenModal(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.7)', border: '1px solid #1f1f1f' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 4l6 3.5L14 4" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" /><rect x="2" y="4" width="12" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.5" /></svg>
                {t('admin.payments.generateCommissions', 'Generate commissions')}
              </button>
              {selectedManagerId && (
                <button
                  onClick={() => setShowModal(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors"
                  style={{ backgroundColor: 'var(--color-primary)' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#cc0000'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = '#E5202A'}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                  Ajouter une transaction
                </button>
              )}
            </div>
          </div>

          {/* ─── KPIs globaux ──────────────────────────────────────────────────── */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-6">
            {[
              { label: t('admin.payments.totalBalance', 'Solde total dû'), value: globalKpis.totalSolde, color: globalKpis.totalSolde >= 0 ? '#22c55e' : '#ef4444', prefix: globalKpis.totalSolde < 0 ? '-' : '' },
              { label: t('admin.payments.commissions', 'Commissions'), value: globalKpis.totalCommissions, color: '#22c55e' },
              { label: t('admin.payments.adjustments', 'Ajustements'), value: globalKpis.totalAdjustments, color: '#f59e0b', prefix: globalKpis.totalAdjustments < 0 ? '-' : globalKpis.totalAdjustments > 0 ? '+' : '' },
              { label: t('admin.payments.tournaments', 'Tournois'), value: globalKpis.totalTournaments, color: '#ec4899', prefix: '-' },
              { label: t('admin.payments.paid', 'Payé'), value: globalKpis.totalPayments, color: '#a855f7' },
            ].map((kpi, i) => (
              <div
                key={i}
                className="rounded-xl p-4 sm:p-5 border"
                style={{ backgroundColor: '#0d0d0d', borderColor: '#1f1f1f' }}
              >
                <span className="text-xs font-medium block mb-2" style={{ color: 'rgba(255,255,255,0.45)', fontFamily: 'Inter, sans-serif' }}>
                  {kpi.label}
                </span>
                <p className="text-xl sm:text-2xl font-bold" style={{ color: kpi.color, fontFamily: 'Sora, sans-serif' }}>
                  {kpi.value < 0 && '-'}{formatCurrency(kpi.value)}
                </p>
              </div>
            ))}
          </div>

          {/* ─── Tableau récap tous managers ───────────────────────────────────── */}
          <div className="rounded-xl border overflow-hidden mb-8" style={{ backgroundColor: '#0d0d0d', borderColor: '#1f1f1f' }}>
            <div className="px-4 sm:px-6 py-4 border-b" style={{ borderColor: '#1f1f1f' }}>
              <h3 className="text-base font-bold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
                {t('admin.payments.summaryByManager', 'Récapitulatif par manager')}
              </h3>
            </div>

            {overviewLoading ? (
              <div className="p-8 text-center" style={{ color: 'rgba(255,255,255,0.3)' }}>Chargement...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr style={{ borderBottom: '1px solid #1f1f1f' }}>
                      {[t('admin.payments.colManager', 'Manager'), t('admin.payments.colCommissions', 'Commissions'), t('admin.payments.colPaid', 'Payé'), t('admin.payments.colBalance', 'Solde dû')].map(h => (
                        <th
                          key={h}
                          className={`px-4 sm:px-6 py-3 text-xs font-medium ${h === 'Manager' ? 'text-left' : 'text-right'}`}
                          style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {overviewData.map(m => {
                      const isSelected = m.id === selectedManagerId;
                      return (
                        <tr
                          key={m.id}
                          className="cursor-pointer transition-colors"
                          style={{
                            borderBottom: '1px solid rgba(255,255,255,0.04)',
                            backgroundColor: isSelected ? 'rgba(229,32,42,0.06)' : 'transparent',
                          }}
                          onClick={() => setSelectedManagerId(m.id)}
                          onMouseEnter={e => { if (!isSelected) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.03)'; }}
                          onMouseLeave={e => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
                        >
                          <td className="px-4 sm:px-6 py-3 text-sm font-medium text-white" style={{ fontFamily: 'Inter, sans-serif' }}>
                            <div className="flex items-center gap-2">
                              {isSelected && <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--color-primary)' }} />}
                              {m.name}
                            </div>
                          </td>
                          <td className="px-4 sm:px-6 py-3 text-sm text-right font-medium" style={{ color: '#22c55e', fontFamily: 'Inter, sans-serif' }}>
                            {formatCurrency(m.commissions)}
                          </td>
                          <td className="px-4 sm:px-6 py-3 text-sm text-right font-medium" style={{ color: m.payments > 0 ? '#a855f7' : 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}>
                            {m.payments > 0 ? formatCurrency(m.payments) : '-'}
                          </td>
                          <td className="px-4 sm:px-6 py-3 text-sm text-right font-bold" style={{ color: m.solde >= 0 ? '#22c55e' : '#ef4444', fontFamily: 'Inter, sans-serif' }}>
                            {m.solde < 0 && '-'}{formatCurrency(m.solde)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ─── Détail manager sélectionné ────────────────────────────────────── */}
          {selectedManagerId && (
            <>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
                  {t('admin.payments.managerCash', 'Caisse de {{name}}', { name: selectedManager?.name })}
                </h2>
                <button
                  onClick={() => setSelectedManagerId('')}
                  className="text-xs px-3 py-1.5 rounded-lg transition-colors"
                  style={{ color: 'rgba(255,255,255,0.4)', border: '1px solid #1f1f1f' }}
                >
                  {t('admin.payments.close', 'Fermer')}
                </button>
              </div>

              {/* KPI Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-6">
                {[
                  { label: 'Solde dû', value: kpis.solde, color: kpis.solde >= 0 ? '#22c55e' : '#ef4444', prefix: kpis.solde < 0 ? '-' : '' },
                  { label: 'Commissions', value: kpis.commissions, color: '#22c55e' },
                  { label: 'Ajustements', value: kpis.adjustments, color: '#f59e0b', prefix: kpis.adjustments < 0 ? '-' : kpis.adjustments > 0 ? '+' : '' },
                  { label: 'Tournois', value: kpis.tournaments, color: '#ec4899', prefix: '-' },
                  { label: 'Payé', value: kpis.payments, color: '#a855f7' },
                ].map((kpi, i) => (
                  <div key={i} className="rounded-xl p-4 border" style={{ backgroundColor: '#0d0d0d', borderColor: '#1f1f1f' }}>
                    <span className="text-xs font-medium block mb-2" style={{ color: 'rgba(255,255,255,0.45)' }}>{kpi.label}</span>
                    <p className="text-lg sm:text-xl font-bold" style={{ color: kpi.color, fontFamily: 'Sora, sans-serif' }}>
                      {kpi.prefix || ''}{formatCurrency(kpi.value)}
                    </p>
                  </div>
                ))}
              </div>

              {/* Historique */}
              <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: '#0d0d0d', borderColor: '#1f1f1f' }}>
                <div className="px-4 sm:px-6 py-4 border-b" style={{ borderColor: '#1f1f1f' }}>
                  <h3 className="text-base font-bold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
                    Historique des transactions
                  </h3>
                </div>

                {loading ? (
                  <div className="p-8 text-center" style={{ color: 'rgba(255,255,255,0.3)' }}>Chargement...</div>
                ) : transactions.length === 0 ? (
                  <div className="p-8 text-center" style={{ color: 'rgba(255,255,255,0.3)' }}>Aucune transaction.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr style={{ borderBottom: '1px solid #1f1f1f' }}>
                          {['Date', 'Type', 'Détail', 'Montant', ''].map(h => (
                            <th
                              key={h}
                              className={`px-4 sm:px-6 py-3 text-xs font-medium ${h === 'Montant' ? 'text-right' : 'text-left'}`}
                              style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map(txn => {
                          const amt = Number(txn.amount) || 0;
                          const TYPE_CONFIG = getTypeConfig(t);
                          const typeConf = TYPE_CONFIG[txn.type] || TYPE_CONFIG.adjustment;
                          const isNeg = amt < 0;
                          return (
                            <tr
                              key={txn.id}
                              className="group transition-colors"
                              style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.02)'}
                              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                              <td className="px-4 sm:px-6 py-3.5 text-sm text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                                {formatDate(txn.created_at)}
                              </td>
                              <td className="px-4 sm:px-6 py-3.5">
                                <span
                                  className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold"
                                  style={{ backgroundColor: typeConf.bg, color: typeConf.color }}
                                >
                                  {typeConf.label}
                                </span>
                              </td>
                              <td className="px-4 sm:px-6 py-3.5 text-sm text-white/80" style={{ fontFamily: 'Inter, sans-serif' }}>
                                {txn.description || '-'}
                              </td>
                              <td className="px-4 sm:px-6 py-3.5 text-sm font-semibold text-right" style={{ color: isNeg ? '#ef4444' : '#22c55e', fontFamily: 'Inter, sans-serif' }}>
                                {isNeg ? '- ' : '+ '}{formatCurrency(amt)}
                              </td>
                              <td className="px-4 sm:px-6 py-3.5">
                                <button
                                  onClick={() => handleDelete(txn.id)}
                                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-red-500/20"
                                  title={t('admin.payments.delete', 'Supprimer')}
                                >
                                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                    <path d="M2 4h10M5 4V2.5A.5.5 0 015.5 2h3a.5.5 0 01.5.5V4M11 4v7.5a1.5 1.5 0 01-1.5 1.5h-5A1.5 1.5 0 013 11.5V4" stroke="#ef4444" strokeWidth="1.2" strokeLinecap="round" />
                                  </svg>
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </main>

      <AdminBottomNav />

      {/* ─── Modal : Ajouter transaction ──────────────────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
          <div
            className="w-full max-w-md rounded-xl p-6 border"
            style={{ backgroundColor: '#111', borderColor: '#1f1f1f' }}
          >
            <h3 className="text-lg font-bold text-white mb-4" style={{ fontFamily: 'Sora, sans-serif' }}>
              Ajouter une transaction
            </h3>

            <div className="mb-4">
              <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.5)' }}>Manager</label>
              <div className="px-3 py-2 rounded-lg text-sm text-white/70" style={{ backgroundColor: '#0d0d0d', border: '1px solid #1f1f1f' }}>
                {selectedManager?.name || '-'}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.5)' }}>Type de transaction</label>
              <select
                value={modalType}
                onChange={e => setModalType(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-base text-white"
                style={{ backgroundColor: '#0d0d0d', border: '1px solid #1f1f1f' }}
              >
                <option value="payment">Paiement (sortie d'argent)</option>
                <option value="adjustment_plus">Ajustement positif (+)</option>
                <option value="adjustment_minus">Ajustement négatif (-)</option>
                <option value="tournament">Tournoi (charge)</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.5)' }}>Date</label>
              <input
                type="date"
                value={modalDate}
                onChange={e => setModalDate(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-base text-white"
                style={{ backgroundColor: '#0d0d0d', border: '1px solid #1f1f1f' }}
              />
            </div>

            <div className="mb-4">
              <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.5)' }}>Montant ($)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={modalAmount}
                onChange={e => setModalAmount(e.target.value)}
                placeholder="0.00"
                className="w-full px-3 py-2 rounded-lg text-base text-white"
                style={{ backgroundColor: '#0d0d0d', border: '1px solid #1f1f1f' }}
              />
            </div>

            <div className="mb-6">
              <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.5)' }}>Description</label>
              <input
                type="text"
                value={modalDescription}
                onChange={e => setModalDescription(e.target.value)}
                placeholder="Ex: Virement PayPal mars 2026"
                className="w-full px-3 py-2 rounded-lg text-base text-white"
                style={{ backgroundColor: '#0d0d0d', border: '1px solid #1f1f1f' }}
              />
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setShowModal(false); setModalAmount(''); setModalDescription(''); setModalDate(new Date().toISOString().split('T')[0]); }}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white/60 hover:text-white transition-colors"
                style={{ border: '1px solid #1f1f1f' }}
              >
                Annuler
              </button>
              <button
                onClick={handleAddTransaction}
                disabled={modalSaving || !modalAmount}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50"
                style={{ backgroundColor: 'var(--color-primary)' }}
              >
                {modalSaving ? 'Enregistrement...' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Modal : Générer commissions ──────────────────────────────────────── */}
      {showGenModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
          <div
            className="w-full max-w-md rounded-xl p-6 border"
            style={{ backgroundColor: '#111', borderColor: '#1f1f1f' }}
          >
            <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Sora, sans-serif' }}>
              Générer les commissions
            </h3>
            <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.4)' }}>
              Calcule et insère les commissions de tous les managers pour la période sélectionnée.
            </p>

            <div className="mb-4">
              <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.5)' }}>Période</label>
              <select
                value={genPeriod}
                onChange={e => setGenPeriod(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-base text-white"
                style={{ backgroundColor: '#0d0d0d', border: '1px solid #1f1f1f' }}
              >
                <option value="">Sélectionner une période</option>
                {availablePeriods.map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>

            {genResult && (
              <div
                className="mb-4 p-3 rounded-lg text-sm"
                style={{
                  backgroundColor: genResult.success ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                  color: genResult.success ? '#22c55e' : '#ef4444',
                  border: `1px solid ${genResult.success ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'}`,
                }}
              >
                {genResult.message}
              </div>
            )}

            <div className="flex flex-col gap-3">
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => { setShowGenModal(false); setGenResult(null); }}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white/60 hover:text-white transition-colors"
                  style={{ border: '1px solid #1f1f1f' }}
                >
                  {t('admin.payments.close', 'Fermer')}
                </button>
                <button
                  onClick={() => generateCommissions(false)}
                  disabled={genLoading || !genPeriod}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50"
                  style={{ backgroundColor: 'var(--color-primary)' }}
                >
                  {genLoading ? 'Calcul en cours...' : 'Générer'}
                </button>
              </div>
              <div className="pt-2 border-t" style={{ borderColor: '#1f1f1f' }}>
                <button
                  onClick={() => generateCommissions(true)}
                  disabled={genLoading || !availablePeriods.length}
                  className="w-full px-4 py-2 rounded-lg text-sm font-medium text-white/70 hover:text-white transition-colors disabled:opacity-50"
                  style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid #1f1f1f' }}
                >
                  {genLoading ? 'Calcul en cours...' : `Générer toutes les périodes (${availablePeriods.length})`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagerPayments;
